import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { SkeletonItem } from 'app/components/DetailsPages/shared/styled';
import GlobalClientDrawer from 'app/components/Drawers/GlobalClient/GlobalClientDrawer';
import GlobalCompanyDrawer from 'app/components/Drawers/GlobalCompany/GlobalCompanyDrawer';
import GlobalIndustryDrawer from 'app/components/Drawers/GlobalIndustry/GlobalIndustryDrawer';
import GlobalPassionDrawer from 'app/components/Drawers/GlobalPassion/GlobalPassionDrawer';
import GlobalPersonDrawer from 'app/components/Drawers/GlobalPerson/GlobalPersonDrawer';
import GlobalProjectDrawer from 'app/components/Drawers/GlobalProject/GlobalProjectDrawer';
import GlobalSkillDrawer from 'app/components/Drawers/GlobalSkill/GlobalSkillDrawer';
import { contentModifier } from 'app/components/TheWell/shared/PublicationContent/Widgets';
import {
  ChatCircleIcon,
  CopyIcon,
  DeleteIcon,
  Spinner,
  ThreeDotsMenuIcon,
} from 'app/components/shared/icons';
import EditPencilIcon from 'app/components/shared/icons/edit-pencil';
import dayjs from 'dayjs';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useDeleteComment from 'hooks/useDeleteComment';
import useUserData from 'hooks/useUserData';
import { MessageableType } from 'services/messages';
import { setResourceModal } from 'store/reducers/global/global-reducer';
import { ThemeProvider } from 'styled-components';

import CompanyLogo from '../CompanyLogo';
import PersonAvatar from '../PersonAvatar';
import { ResourceModalHandlerContext } from '../ResourceModalHandler/ResourceModalHandlerContext';
import ShareBarLikeCTA from '../ShareBar/ShareBarLikeCTA';
import SideDrawer from '../SideDrawer';
import SubmitComment from '../SubmitComment';
import {
  CommentActionMenuContainer,
  CommentActionMenuItem,
  CommentActionMenuItemLabel,
  CommentActionMenuWrapper,
  CommentBottomChildMessagesCounter,
  CommentBottomReply,
  CommentBottomWrapper,
  CommentBottomWrapperIconContainer,
  CommentBottomWrapperIconsWrapper,
  CommentContent,
  CommentDeletedStateText,
  CommentHeaderDate,
  CommentHeaderPersonInfoName,
  CommentHeaderPersonInfoRole,
  CommentHeaderPersonInfoTextWrapper,
  CommentHeaderPersonInfoWrapper,
  CommentHeaderRightContainer,
  CommentHeaderRightContainerIconWrapper,
  CommentHeaderWrapper,
  CommentWrapper,
} from './styled';

export interface IComment {
  uuid: string;
  content: string;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  person: {
    first_name: string;
    last_name: string;
    full_name: string;
    uuid: string;
    slug: string;
    company_name: string;
    role_name: string;
    hide_alumni_label: boolean;
    avatar: string;
    is_alumni: boolean;
  };
  child_messages: IComment[];
}

interface CommentProps {
  comment: IComment;
  onReply?: (comment: IComment) => void;
  onDelete?: (comment: IComment) => void;
  messageable_type?: MessageableType;
  messageable_uuid_or_slug?: string;
  parent_uuid?: string;
  onShowChildMessages?: () => void;
}

export default function Comment({
  comment,
  onReply,
  onDelete,
  messageable_type,
  messageable_uuid_or_slug,
  parent_uuid,
  onShowChildMessages,
}: CommentProps) {
  const { push, location } = useHistory();
  const dispatch = useDispatch();
  const commentWrapperRef = useRef<HTMLDivElement>(null);
  const { mutate: deleteComment, isLoading: isDeleting } = useDeleteComment();
  const [commentContent, setCommentContent] = useState<string>(comment.content);
  const [drawer, setDrawer] = useState({ show: false, slug: '', type: null });
  const [dataTypeData, setDataTypeData] = useState<any>({ slug: '', type: null });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDeleteState, setShowDeleteState] = useState<boolean>(!!comment.deleted_at);
  const [showActionMenu, setShowActionMenu] = useState<boolean>(false);
  const [aboutToDelete, setAboutToDelete] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const messageUuid = params.get('message');

    const matches = messageUuid === comment.uuid && !comment.deleted_at;
    setIsHighlighted(matches);
    if (matches) {
      commentWrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [location.search, comment.uuid, comment.deleted_at]);

  const publicationContentRef = useCallback(
    (node: HTMLDivElement) =>
      contentModifier({
        node,
        setDrawer,
        push,
        setDataTypeData,
        openResourceModal: (slug) => dispatch(setResourceModal({ show: true, slug })),
      }),
    [comment.content, dispatch]
  );

  useOnClickOutside({ current: popperElement }, (e) => {
    const target = e.target as HTMLElement;
    if (
      showActionMenu &&
      !target.closest(`[data-id="comment-action-menu-icon-wrapper-${comment.uuid}"]`)
    ) {
      setShowActionMenu(false);
      setAboutToDelete(false);
    }
  });

  const handleShowActionMenu = () => {
    setShowActionMenu(!showActionMenu);
  };

  const handleEditComment = () => {
    setIsEditing(true);
    setShowActionMenu(false);
  };

  const handleDeleteComment = () => {
    deleteComment(comment.uuid, {
      onSuccess: () => {
        if (comment.child_messages.length === 0) {
          onDelete?.(comment);
        } else {
          setShowActionMenu(false);
          setShowDeleteState(true);
        }
      },
    });
  };

  const handleAboutToDelete = () => {
    setAboutToDelete(true);
  };

  const handleReply = () => {
    onReply?.(comment);
  };

  const handleEditFinish = (editedComment: IComment) => {
    setCommentContent(editedComment.content);
    setIsEditing(false);
  };

  const handleCopyLink = () => {
    setShowActionMenu(false);
  };

  const handleShowChildMessages = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    onShowChildMessages?.();
  };

  const theme = {
    aboutToDelete,
    hasReply: comment.child_messages.length > 0,
    showDeleteState,
    isHighlighted,
  };

  return (
    <ThemeProvider theme={theme}>
      <CommentWrapper ref={commentWrapperRef} data-message-uuid={comment.uuid} data-highlighted={isHighlighted}>
        <AnimatePresence>
          {!showDeleteState ? (
            <CommentHeaderWrapper>
              <CommentHeaderPersonInfoWrapper>
                <PersonAvatar
                  size={40}
                  avatar={comment.person.avatar}
                  name={comment.person.first_name}
                  lastName={comment.person.last_name}
                  highlighted
                />
                <CommentHeaderPersonInfoTextWrapper>
                  <CommentHeaderPersonInfoName>
                    {comment.person.first_name} {comment.person.last_name}
                  </CommentHeaderPersonInfoName>
                  <CommentHeaderPersonInfoRole>
                    {comment.person.role_name} at{' '}
                    <CompanyLogo size={20} slug={comment.person.company_name} useRounded />{' '}
                    {comment.person.company_name}
                  </CommentHeaderPersonInfoRole>
                </CommentHeaderPersonInfoTextWrapper>
              </CommentHeaderPersonInfoWrapper>
              <CommentHeaderRightContainer>
                <CommentHeaderDate>
                  {dayjs(comment.created_at).format('ddd MMM D, YYYY h:mmA')}
                </CommentHeaderDate>
                <CommentHeaderRightContainerIconWrapper
                  data-id={`comment-action-menu-icon-wrapper-${comment.uuid}`}
                  onClick={handleShowActionMenu}
                  ref={setReferenceElement}
                >
                  <ThreeDotsMenuIcon />
                </CommentHeaderRightContainerIconWrapper>
              </CommentHeaderRightContainer>
            </CommentHeaderWrapper>
          ) : (
            <CommentDeletedStateText>
              This comment was deleted by the author
            </CommentDeletedStateText>
          )}
        </AnimatePresence>
        {!showDeleteState ? (
          <>
            {!isEditing ? (
              <CommentContent
                ref={publicationContentRef}
                dangerouslySetInnerHTML={{ __html: commentContent }}
              />
            ) : (
              <SubmitComment
                editing
                messageable_type={messageable_type}
                messageable_uuid_or_slug={messageable_uuid_or_slug}
                parent_uuid={parent_uuid}
                initialValue={commentContent}
                message_uuid={comment.uuid}
                onFinish={handleEditFinish}
              />
            )}
          </>
        ) : null}
        <CommentBottomWrapper>
          <CommentBottomWrapperIconsWrapper>
            {!showDeleteState ? (
              <CommentBottomWrapperIconContainer data-id="comment-like-icon">
                <ShareBarLikeCTA hideOnboarding slug={comment.uuid} type="message">
                  {(props) => {
                    const text = props.text?.toString().toLowerCase();
                    if (!text || text === 'like' || text === '0') return null;

                    if (text === 'you') return 1;

                    return props.text;
                  }}
                </ShareBarLikeCTA>
              </CommentBottomWrapperIconContainer>
            ) : null}
            <CommentBottomWrapperIconContainer onClick={handleReply} data-id="comment-reply-icon">
              <ChatCircleIcon
                width={24}
                height={24}
                fillOpacity={comment.child_messages.length > 0 ? 1 : 0.5}
              />
              <CommentBottomReply>REPLY</CommentBottomReply>
              {comment.child_messages.length > 0 && (
                <CommentBottomChildMessagesCounter onClick={handleShowChildMessages}>
                  | {comment.child_messages.length}
                </CommentBottomChildMessagesCounter>
              )}
            </CommentBottomWrapperIconContainer>
          </CommentBottomWrapperIconsWrapper>
        </CommentBottomWrapper>
        <AnimatePresence>
          {showActionMenu && (
            <CommentActionMenuWrapper
              ref={setPopperElement}
              style={{ ...styles.popper }}
              {...attributes.popper}
            >
              <Comment.ActionMenu
                comment={comment}
                onDelete={handleDeleteComment}
                onEdit={handleEditComment}
                aboutToDelete={handleAboutToDelete}
                isDeleting={isDeleting}
                onCopyLink={handleCopyLink}
                messageable_uuid_or_slug={messageable_uuid_or_slug}
              />
            </CommentActionMenuWrapper>
          )}
        </AnimatePresence>
      </CommentWrapper>
      {drawer.show && (
        <SideDrawer closeDrawer={() => setDrawer({ show: false, slug: '', type: null })} withClose>
          <>
            {drawer.type === 'people' ? <GlobalPersonDrawer slug={drawer.slug} /> : null}
            {drawer.type === 'passions' ? <GlobalPassionDrawer slug={drawer.slug} /> : null}
            {drawer.type === 'skills' ? <GlobalSkillDrawer slug={drawer.slug} /> : null}
            {drawer.type === 'clients' ? <GlobalClientDrawer slug={drawer.slug} /> : null}
            {drawer.type === 'projects' ? <GlobalProjectDrawer slug={drawer.slug} /> : null}
            {drawer.type === 'industries' ? <GlobalIndustryDrawer slug={drawer.slug} /> : null}
            {drawer.type === 'member_companies' ? <GlobalCompanyDrawer slug={drawer.slug} /> : null}
          </>
        </SideDrawer>
      )}
    </ThemeProvider>
  );
}

interface CommentActionMenuProps {
  onDelete: () => void;
  onEdit: () => void;
  aboutToDelete?: () => void;
  isDeleting: boolean;
  onCopyLink?: () => void;
  comment: IComment;
  messageable_uuid_or_slug?: string;
}

Comment.ActionMenu = function CommentActionMenu({
  onDelete,
  onEdit,
  aboutToDelete,
  isDeleting,
  onCopyLink,
  comment,
  messageable_uuid_or_slug,
}: CommentActionMenuProps) {
  const userData = useUserData();
  const resourceModalContext = useContext(ResourceModalHandlerContext);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  const isOwnedByUser = useMemo(() => {
    return comment.person.uuid === userData?.uuid;
  }, [comment.person.uuid, userData?.uuid]);

  const handleDelete = () => {
    if (confirmDelete) {
      onDelete();
    } else {
      setConfirmDelete(true);
      aboutToDelete?.();
    }
  };

  const handleEdit = () => {
    onEdit();
  };

  const handleCopyLink = () => {
    const url = new URL(
      window.location.pathname === '/resources'
        ? `${window.location.origin}/resources/${messageable_uuid_or_slug}`
        : resourceModalContext?.settings?.shareLink
        ? resourceModalContext.settings.shareLink
        : window.location.href
    );
    url.searchParams.set('message', comment.uuid);
    navigator.clipboard.writeText(url.toString());

    onCopyLink?.();
  };

  return (
    <CommentActionMenuContainer>
      {isOwnedByUser ? (
        <CommentActionMenuItem onClick={handleEdit}>
          <EditPencilIcon width={18} height={18} />
          <CommentActionMenuItemLabel>Edit</CommentActionMenuItemLabel>
        </CommentActionMenuItem>
      ) : null}
      {isOwnedByUser ? (
        <CommentActionMenuItem onClick={handleDelete}>
          {!confirmDelete ? (
            <DeleteIcon width={18} height={18} fill="var(--color-error)" />
          ) : isDeleting ? (
            <Spinner width={18} height={18} fill="var(--color-error)" />
          ) : null}
          <CommentActionMenuItemLabel style={{ color: 'var(--color-error)' }}>
            {!confirmDelete ? 'Delete' : 'Click to confirm'}
          </CommentActionMenuItemLabel>
        </CommentActionMenuItem>
      ) : null}
      <CommentActionMenuItem onClick={handleCopyLink}>
        <CopyIcon width={18} height={18} />
        <CommentActionMenuItemLabel>Copy link</CommentActionMenuItemLabel>
      </CommentActionMenuItem>
    </CommentActionMenuContainer>
  );
};

Comment.Skeleton = function CommentSkeleton() {
  return (
    <CommentWrapper>
      <CommentHeaderWrapper>
        <CommentHeaderPersonInfoWrapper>
          <SkeletonItem animated style={{ width: '40px', height: '40px', borderRadius: '100%' }} />
        </CommentHeaderPersonInfoWrapper>
      </CommentHeaderWrapper>
    </CommentWrapper>
  );
};
